// S4 · the per-row grid (Module 02 §4 S4, §2.7, §7).
//
// Status filter chips (all / OK / warnings / errors / loaded / skipped), a free
// text search over the raw cells, cursor pagination, and per row the full
// message list with its severity and the field it points at — every message
// resolved from its CODE through the dictionary, never printed raw.
//
// §7 redaction: money columns arrive `null` with their path listed in the
// response's `redacted_fields[]`. Those cells render a LOCK with a screen-reader
// explanation — never a blank (which reads as "the file had nothing there") and
// never a zero. The page also carries one line saying money is hidden, so the
// user knows the import still carries values they cannot see.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useImportRows, type ImportRowsQuery } from "../../hooks/onboarding-queries";
import {
  cellText,
  groupFirstHeaders,
  hasRedaction,
  isRedactedCell,
  sourceHeaders,
  type ImportJob,
  type ImportRow,
  type ImportRowStatus,
} from "./import-model";
import { useImportLabels } from "./labels";

const PAGE_SIZE = 50;
/** Wide sheets stay readable: the grid shows the first columns and points at the
 * error report for the rest (the report carries every original column, §3.3.4). */
const MAX_COLUMNS = 8;

type RowFilter = "all" | ImportRowStatus;

const FILTERS: readonly { value: RowFilter; labelKey: string }[] = [
  { value: "all", labelKey: "onboarding.importDetail.rows.filterAll" },
  { value: "valid", labelKey: "onboarding.importDetail.rows.filterValid" },
  { value: "warning", labelKey: "onboarding.importDetail.rows.filterWarning" },
  { value: "error", labelKey: "onboarding.importDetail.rows.filterError" },
  { value: "committed", labelKey: "onboarding.importDetail.rows.filterCommitted" },
  { value: "skipped", labelKey: "onboarding.importDetail.rows.filterSkipped" },
];

export function ImportRowGrid({ job }: { job: ImportJob }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<RowFilter>("all");
  const [search, setSearch] = useState("");
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);

  // The input stays instant; only the value that reaches the QUERY KEY waits.
  // Each keystroke otherwise cost one `GET …/rows?q=`, and each of those is a
  // substring scan over a table that can hold a million rows (§2.7).
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  const cursor = cursors[cursors.length - 1];
  const query: ImportRowsQuery = { limit: PAGE_SIZE };
  if (cursor !== undefined) query.cursor = cursor;
  if (filter !== "all") query.status = filter;
  if (debouncedSearch.length > 0) query.q = debouncedSearch;

  const rows = useImportRows(job.id, query);

  function resetPaging() {
    setCursors([undefined]);
  }

  const items = rows.data?.items ?? [];
  const redacted = rows.data?.redacted_fields ?? [];
  const nextCursor = rows.data?.next_cursor ?? null;
  // A grouped lane leads with its group key's column, so each row's order
  // shows beside it however wide the sheet is.
  const allColumns = groupFirstHeaders(job.kind, job.column_mapping, sourceHeaders({}, items));
  const columns = allColumns.slice(0, MAX_COLUMNS);
  const hiddenColumns = Math.max(0, allColumns.length - columns.length);

  const counts: Record<RowFilter, number | undefined> = {
    all: job.total_rows,
    valid: job.valid_rows,
    warning: job.warning_rows,
    error: job.error_rows,
    committed: job.committed_rows,
    skipped: undefined,
    pending: undefined,
  };

  return (
    <section className="import-rows" data-testid="import-row-grid">
      <header className="import-section-head">
        <h3>{t("onboarding.importDetail.rows.title")}</h3>
      </header>

      <div className="import-row-toolbar">
        <div
          className="import-chips"
          role="group"
          aria-label={t("onboarding.importDetail.rows.title")}
        >
          {FILTERS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              className={`import-chip${filter === chip.value ? " is-active" : ""}`}
              aria-pressed={filter === chip.value}
              onClick={() => {
                setFilter(chip.value);
                resetPaging();
              }}
            >
              {t(chip.labelKey)}
              {counts[chip.value] !== undefined ? (
                <span className="import-chip-count">{counts[chip.value]}</span>
              ) : null}
            </button>
          ))}
        </div>
        <label className="search-field">
          <span className="visually-hidden">{t("onboarding.importDetail.rows.searchLabel")}</span>
          <input
            className="ds-input"
            type="search"
            value={search}
            placeholder={t("onboarding.importDetail.rows.searchPlaceholder")}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPaging();
            }}
          />
        </label>
      </div>

      {hasRedaction(redacted) ? (
        <p className="import-redaction-note" role="note">
          {t("onboarding.importShared.redactedNotice")}
        </p>
      ) : null}

      <RowGridBody
        kind={job.kind}
        query={rows}
        items={items}
        columns={columns}
        redacted={redacted}
        filtered={filter !== "all" || debouncedSearch.length > 0}
        onClearFilters={() => {
          setFilter("all");
          setSearch("");
          resetPaging();
        }}
      />

      {hiddenColumns > 0 ? (
        <p className="wizard-save-bar-note">
          {t("onboarding.importDetail.rows.moreColumns", { count: hiddenColumns })}
        </p>
      ) : null}

      {items.length > 0 ? (
        <nav className="pagination" aria-label={t("onboarding.importDetail.rows.next")}>
          <Button
            disabled={cursors.length <= 1}
            onClick={() => {
              setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c));
            }}
          >
            {t("onboarding.importDetail.rows.prev")}
          </Button>
          <span className="pagination-info">
            {t("onboarding.importDetail.rows.showing", { count: items.length })}
          </span>
          <Button
            disabled={nextCursor === null}
            onClick={() => {
              if (nextCursor !== null) setCursors((c) => [...c, nextCursor]);
            }}
          >
            {t("onboarding.importDetail.rows.next")}
          </Button>
        </nav>
      ) : null}
    </section>
  );
}

interface RowGridBodyProps {
  kind: ImportJob["kind"];
  query: ReturnType<typeof useImportRows>;
  items: readonly ImportRow[];
  columns: readonly string[];
  redacted: readonly string[];
  filtered: boolean;
  onClearFilters: () => void;
}

function RowGridBody({
  kind,
  query,
  items,
  columns,
  redacted,
  filtered,
  onClearFilters,
}: RowGridBodyProps) {
  const { t } = useTranslation();
  const labels = useImportLabels();

  if (query.isPending) {
    return (
      <TableSkeleton
        rows={8}
        cols={Math.max(3, columns.length)}
        label={t("onboarding.importDetail.rows.loading")}
      />
    );
  }

  if (query.error !== null) {
    return (
      <ErrorState
        title={t("onboarding.importDetail.rows.errorTitle")}
        // Translated copy, not the raw server/JS message (the i18n rule).
        detail={t("onboarding.importDetail.rows.errorDetail")}
        retryLabel={t("onboarding.state.retry")}
        onRetry={() => void query.refetch()}
        requestId={requestIdOf(query.error)}
        requestIdLabel={t("onboarding.state.requestId")}
      />
    );
  }

  if (items.length === 0) {
    return filtered ? (
      <EmptyState
        title={t("onboarding.importDetail.rows.noMatchTitle")}
        body={t("onboarding.importDetail.rows.noMatchBody")}
        ctaLabel={t("onboarding.importDetail.rows.filterAll")}
        onCta={onClearFilters}
      />
    ) : (
      <EmptyState
        title={t("onboarding.importDetail.rows.emptyTitle")}
        body={t("onboarding.importDetail.rows.emptyBody")}
      />
    );
  }

  return (
    <>
      {/* Desktop grid — UX-6b: DataTable (D-219). The spreadsheet columns are
          server DATA (the file's own headers), so the column list is BUILT per
          render — dynamic columns are just `columns` array elements, not a
          reason for a raw table. Dual render stays: this wrapper hides at ≤768
          (`.import-table-scroll`) and the card list below takes over, exactly
          the geometry the certified M02 responsive net pins. */}
      <div className="import-table-scroll">
        <DataTable
          columns={[
            {
              key: "row_no",
              header: t("onboarding.importDetail.rows.colRow"),
              numeric: true,
              render: (row) => row.row_no,
            },
            {
              key: "status",
              header: t("onboarding.importDetail.rows.colStatus"),
              render: (row) => (
                <span className={`badge status-${row.status}`}>
                  {labels.rowStatusLabel(row.status)}
                </span>
              ),
            },
            ...columns.map((column): DataTableColumn<ImportRow> => ({
              key: `raw:${column}`,
              header: column,
              render: (row) => <ImportRowCell row={row} column={column} redacted={redacted} />,
            })),
            {
              key: "messages",
              header: t("onboarding.importDetail.rows.colMessages"),
              render: (row) => <ImportRowMessages row={row} kind={kind} />,
            },
          ]}
          rows={items}
          rowKey={(row) => row.id}
          empty={null}
          rowTestId="import-row"
          rowProps={(row) => ({ "data-status": row.status })}
        />
      </div>

      {/* Mobile cards (§4: tables collapse to cards ≤768px) */}
      <ul className="import-row-cards">
        {items.map((row) => (
          <li key={row.id} className="import-row-card" data-testid="import-row-card">
            <div className="import-row-card-head">
              <span className="import-row-card-no">
                {t("onboarding.importDetail.rows.rowLabel", { number: row.row_no })}
              </span>
              <span className={`badge status-${row.status}`}>
                {labels.rowStatusLabel(row.status)}
              </span>
            </div>
            <dl className="import-row-card-cells">
              {columns.map((column) => (
                <div key={column}>
                  <dt>{column}</dt>
                  <dd>
                    <ImportRowCell row={row} column={column} redacted={redacted} />
                  </dd>
                </div>
              ))}
            </dl>
            <ImportRowMessages row={row} kind={kind} />
          </li>
        ))}
      </ul>
    </>
  );
}

export function ImportRowCell({
  row,
  column,
  redacted,
}: {
  row: ImportRow;
  column: string;
  redacted: readonly string[];
}) {
  const { t } = useTranslation();
  const value = row.raw[column];
  if (isRedactedCell(redacted, "raw", column, value ?? null)) {
    return (
      <span className="import-cell-redacted" data-testid="redacted-cell">
        <span aria-hidden="true">🔒</span>
        <span className="visually-hidden">{t("onboarding.importShared.redactedHint")}</span>
      </span>
    );
  }
  return <>{cellText(value)}</>;
}

export function ImportRowMessages({ row, kind }: { row: ImportRow; kind?: ImportJob["kind"] }) {
  const { t } = useTranslation();
  const labels = useImportLabels();
  if (row.messages.length === 0) {
    return <span className="import-row-ok">{t("onboarding.importDetail.rows.messagesNone")}</span>;
  }
  return (
    <ul className="import-row-messages">
      {row.messages.map((message, index) => (
        <li key={`${message.code}-${String(message.field)}-${String(index)}`}>
          <span className={`badge severity-${message.severity}`}>
            {labels.severityLabel(message.severity)}
          </span>
          {message.field !== null ? (
            <span className="import-message-field">{labels.fieldLabel(message.field)}</span>
          ) : null}
          <span className="import-message-text">{labels.messageText(message, kind)}</span>
        </li>
      ))}
    </ul>
  );
}
